import { useState } from "react";
import { ArrowLeft, ArrowRight, Home, CalendarClock, X, Plus } from "lucide-react";
import StickyFooterCTA from "../ui/StickyFooterCTA";

// Iter AO — WrapUpSection is rendered ONLY under the EOT "Advanced / Edit"
// collapsible. The single verbal debrief lives on `tx-notes`; the AI
// Recommendations bundle auto-generates from it. This component no longer
// asks the therapist to describe the session again — it just exposes the
// editable home-care list + return-plan cards that flow into the SOAP/PDF
// report for compliance editing.

function ReturnCard({ tone, label, plan }) {
  const accent = tone === "best"
    ? "border-sage-500/40 bg-sage-500/5"
    : "border-terracotta-500/30 bg-terracotta-500/5";
  const badge = tone === "best"
    ? "bg-sage-500 text-white"
    : "bg-terracotta-500 text-white";
  return (
    <div className={`rounded-2xl border p-5 ${accent}`} data-testid={`return-card-${tone}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-[10px] uppercase tracking-[0.14em] font-medium px-2.5 py-1 rounded-full ${badge}`}>{label}</span>
        <CalendarClock className="w-4 h-4 text-ink-700" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-2xl font-light text-ink-900 mb-2">{plan?.when || "\u2014"}</div>
      <p className="text-sm text-ink-700 leading-relaxed">{plan?.why || ""}</p>
    </div>
  );
}

export default function WrapUpSection({ wrapUp, setWrapUp, setPhase }) {
  const [newItem, setNewItem] = useState("");

  const removeItem = (i) => setWrapUp({ ...wrapUp, home_care: wrapUp.home_care.filter((_, idx) => idx !== i) });
  const addItem = () => {
    const t = newItem.trim();
    if (!t) return;
    setWrapUp({ ...wrapUp, home_care: [...(wrapUp.home_care || []), t] });
    setNewItem("");
  };
  const editReturn = (key, field, value) => setWrapUp({ ...wrapUp, [key]: { ...(wrapUp[key] || {}), [field]: value } });

  return (
    <div data-testid="wrapup-phase">
      <div className="label-overline mb-2">Wrap-up details (manual edit)</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-ink-900 mb-2">Home care &amp; return plan</h2>
      <p className="text-ink-700 mb-6 max-w-2xl text-sm">
        Tweak the home-care items or the suggested return cadence if you want the PDF report to reflect something different from the AI bundle above.
      </p>

      {/* Home care list */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6" data-testid="wrapup-home-care-card">
        <div className="flex items-center gap-2 mb-4">
          <Home className="w-4 h-4 text-sage-600" strokeWidth={1.75} />
          <div className="label-overline">Home care for the client</div>
        </div>
        <ul className="space-y-2 mb-4">
          {(wrapUp.home_care || []).map((item, i) => (
            <li key={`hc-${i}-${item.slice(0, 20)}`} className="flex items-start gap-3 bg-paper-50 rounded-xl px-4 py-3" data-testid={`home-care-item-${i}`}>
              <span className="w-5 h-5 rounded-full bg-sage-500 text-white text-xs flex items-center justify-center shrink-0 mt-0.5 font-medium">{i + 1}</span>
              <span className="flex-1 text-sm text-ink-900 leading-relaxed">{item}</span>
              <button onClick={() => removeItem(i)} className="text-ink-500 hover:text-destructive shrink-0" aria-label="Remove" data-testid={`home-care-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
          {(wrapUp.home_care || []).length === 0 && (
            <li className="text-sm text-ink-500 italic">No home-care items yet \u2014 use the AI bundle above or add some below.</li>
          )}
        </ul>
        <div className="flex items-center gap-2">
          <input
            data-testid="home-care-new-input"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
            placeholder="Add another suggestion\u2026"
            className="flex-1 min-h-[40px] px-3 rounded-lg bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
          />
          <button onClick={addItem} className="w-10 h-10 rounded-full bg-paper-100 hover:bg-paper-200 text-ink-700 flex items-center justify-center transition-all" aria-label="Add" data-testid="home-care-add-btn">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Return plan */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="space-y-2">
          <ReturnCard tone="best" label="Best \u2014 recommended" plan={wrapUp.best_return} />
          <ReturnEditor
            value={wrapUp.best_return || {}}
            onChange={(field, v) => editReturn("best_return", field, v)}
            testId="best-return-editor"
          />
        </div>
        <div className="space-y-2">
          <ReturnCard tone="good" label="Good alternative" plan={wrapUp.good_return} />
          <ReturnEditor
            value={wrapUp.good_return || {}}
            onChange={(field, v) => editReturn("good_return", field, v)}
            testId="good-return-editor"
          />
        </div>
      </div>

      {setPhase && (
        <StickyFooterCTA testid="wrapup-footer" className="justify-between">
          <button onClick={() => setPhase("diagram")} className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={() => setPhase("soap")} className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2" data-testid="to-soap-btn">
            SOAP notes <ArrowRight className="w-4 h-4" />
          </button>
        </StickyFooterCTA>
      )}
    </div>
  );
}

function ReturnEditor({ value, onChange, testId }) {
  return (
    <details className="bg-white border border-paper-200 rounded-xl px-4 py-2 text-xs text-ink-700">
      <summary className="cursor-pointer py-1 select-none">Edit</summary>
      <div className="grid grid-cols-1 gap-2 py-2">
        <input
          data-testid={`${testId}-when`}
          value={value.when || ""}
          onChange={(e) => onChange("when", e.target.value)}
          placeholder="When (e.g. 3 days)"
          className="min-h-[36px] px-3 rounded-lg bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
        />
        <textarea
          data-testid={`${testId}-why`}
          value={value.why || ""}
          onChange={(e) => onChange("why", e.target.value)}
          rows={2}
          placeholder="Clinical reasoning"
          className="px-3 py-2 rounded-lg bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y"
        />
      </div>
    </details>
  );
}
